import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, ChevronUp } from 'lucide-react';

export const Footer = () => {
  const [expanded, setExpanded] = useState(false);
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Training Delivery Dashboard', path: '/dashboard' },
    { label: 'Private Deliveries', path: '/training-projects' },
    { label: 'Courses', path: '/courses' },
    { label: 'Partners', path: '/partners' },
  ];

  const resources = [
    { label: 'Skilling Hub', url: 'https://skilling-hub.com', external: true },
    { label: 'LevelUp Courses', url: 'https://skillupwithlevelup.com/courses', external: true },
    { label: 'Microsoft Learn', url: 'https://learn.microsoft.com', external: true },
    { label: 'Partner Center', url: 'https://partner.microsoft.com', external: true },
  ];

  const support = [
    { label: 'Contact Us', url: 'mailto:contact@technofocus.ai', type: 'email' },
    { label: 'Help Center', path: '/help-center', type: 'internal' },
    { label: 'Privacy Policy', url: 'https://technofocus.ai/privacy', type: 'external' },
    { label: 'Terms of Service', path: '/terms-of-service', type: 'internal' },
  ];

  return (
    <footer className="bg-secondary text-white mt-auto" data-testid="app-footer">
      {/* Slim bar — always visible on every page */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/skilltrac-logo-white.svg" alt="Skilltrac" className="h-4 w-auto flex-shrink-0" />
          <span className="text-white/40 text-xs truncate hidden sm:inline">© {currentYear} Technofocus Pte Ltd. All rights reserved.</span>
        </div>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-1.5 text-white/60 hover:text-lime text-xs font-medium transition-colors flex-shrink-0"
          data-testid="footer-expand-toggle"
        >
          {expanded ? 'Show less' : 'Links & resources'}
          <ChevronUp className={`w-3.5 h-3.5 transition-transform ${expanded ? '' : 'rotate-180'}`} />
        </button>
      </div>

      {/* Full footer — collapsed by default, expands only when requested */}
      {expanded && (
        <div className="border-t border-white/10" data-testid="footer-expanded-panel">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Brand Section */}
              <div className="lg:col-span-1">
                <div className="space-y-4">
                  <img
                    src="/skilltrac-logo-white.svg"
                    alt="Skilltrac"
                    className="h-10 w-auto"
                  />
                  <p className="text-white/70 text-sm leading-relaxed">
                    Training Delivery Management System for Microsoft Partners
                  </p>
                  <div className="pt-2">
                    <a
                      href="https://technofocus.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-white/70 hover:text-lime transition-colors"
                    >
                      <img
                        src="/technofocus-logo.png"
                        alt="Technofocus"
                        className="h-8 w-auto brightness-0 invert"
                      />
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="font-semibold text-white mb-4">Quick Links</h3>
                <ul className="space-y-3">
                  {quickLinks.map((link) => (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        className="text-white/70 hover:text-lime transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h3 className="font-semibold text-white mb-4">Resources</h3>
                <ul className="space-y-3">
                  {resources.map((link) => (
                    <li key={link.url}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/70 hover:text-lime transition-colors text-sm inline-flex items-center gap-1"
                      >
                        {link.label}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Support */}
              <div>
                <h3 className="font-semibold text-white mb-4">Support</h3>
                <ul className="space-y-3">
                  {support.map((link) => (
                    <li key={link.label}>
                      {link.type === 'email' ? (
                        <a
                          href={link.url}
                          className="text-white/70 hover:text-lime transition-colors text-sm inline-flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" />
                          {link.label}
                        </a>
                      ) : link.type === 'external' ? (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/70 hover:text-lime transition-colors text-sm inline-flex items-center gap-1"
                        >
                          {link.label}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <Link
                          to={link.path}
                          className="text-white/70 hover:text-lime transition-colors text-sm"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Powered by logos */}
            <div className="border-t border-white/10 mt-10 pt-8 flex items-center justify-center gap-6">
              <span className="text-white/40 text-xs">Powered by</span>
              <a
                href="https://technofocus.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="/technofocus-logo.png"
                  alt="Technofocus"
                  className="h-6 w-auto brightness-0 invert"
                />
              </a>
              <span className="text-white/20">|</span>
              <Link to="/dashboard" className="hover:opacity-80 transition-opacity">
                <img
                  src="/skilltrac-logo-white.svg"
                  alt="Skilltrac"
                  className="h-6 w-auto"
                />
              </Link>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
